import { motion } from "framer-motion";
import {
  TrendingUp,
  Clock,
  DollarSign,
  Shield,
  Users,
  BarChart3,
  Target,
  Zap,
} from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";

const benefits = [
  {
    icon: Target,
    title: "%95+ Tanıma Doğruluğu",
    description: "Tıbbi terminolojiye özel eğitilmiş AI ile endüstri lideri performans",
    metric: "%95+",
    metricLabel: "Doğruluk",
    gradient: "from-primary-500 to-indigo-600",
  },
  {
    icon: Clock,
    title: "%90+ Zaman Tasarrufu",
    description: "~200ms inference süresi ile rapor oluşturmada dramatik hızlanma",
    metric: "~200ms",
    metricLabel: "Inference",
    gradient: "from-emerald-500 to-teal-600",
  },
  {
    icon: DollarSign,
    title: "$0.03 / Rapor",
    description: "AI tabanlı hibrit yaklaşım ile düşük maliyet, yüksek performans",
    metric: "$0.03",
    metricLabel: "Rapor başı",
    gradient: "from-amber-500 to-orange-600",
  },
  {
    icon: BarChart3,
    title: "%75 Önbellek Başarısı",
    description: "Akıllı önbellekleme ile isteklerin 3/4'ü <1ms'de karşılanır",
    metric: "%75",
    metricLabel: "Önbellek",
    gradient: "from-sky-500 to-blue-600",
  },
  {
    icon: Users,
    title: "1,200 Eşzamanlı",
    description: "Yalnızca 1.8GB bellek kullanımı ile yüksek ölçeklenebilirlik",
    metric: "1,200",
    metricLabel: "Kullanıcı",
    gradient: "from-violet-500 to-purple-600",
  },
  {
    icon: Shield,
    title: "%100 Uyumluluk",
    description: "KVKK, ISO 27001, ISO 27799, OWASP standartlarına tam uyum",
    metric: "%100",
    metricLabel: "Uyumluluk",
    gradient: "from-teal-500 to-cyan-600",
  },
];

const additionalBenefits = [
  {
    icon: Shield,
    title: "İnsan Hatalarının Azaltılması",
    description: "Yüksek doğruluklu AI tanıma ile manuel veri girişi hatalarını minimize eder",
  },
  {
    icon: Target,
    title: "Kritik Bilgi Kaybının Önlenmesi",
    description: "%97 doğrulama başarısı ile önemli bulgular kaybolmaz",
  },
  {
    icon: BarChart3,
    title: "Standardize Veri Formatı",
    description: "SNOMED CT + ICD-10 ile uluslararası standartlara uygun veri",
  },
  {
    icon: Users,
    title: "Gelişmiş Hasta Yönetimi",
    description: "K-Anonymity k≥5 ile hasta gizliliğini koruyarak erişim",
  },
];

export default function Benefits() {
  return (
    <Section id="benefits" background="white" padding="xl">
      <div className="text-center mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Ölçülebilir{" "}
            <span className="text-gradient">Sonuçlar</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            1,900 gerçek tıbbi rapor üzerinde doğrulanmış, 7/7 metrikte hedefe ulaşmış performans
          </p>
        </motion.div>
      </div>

      {/* Main Benefits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
        {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.07 }}
            >
              <Card className="h-full group text-center" variant="glass">
                <div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.gradient} mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon size={26} className="text-white" />
                </div>

                <div className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  {benefit.metric}
                </div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-3">
                  {benefit.metricLabel}
                </div>

                <h3 className="text-base font-display font-semibold text-gray-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {benefit.description}
                </p>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Additional Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-14"
      >
        <h3 className="text-xl font-display font-bold text-center text-gray-900 dark:text-white mb-8">
          Ek Faydalar
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {additionalBenefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <Card variant="glass" padding="sm" className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/40 dark:to-primary-800/30 flex items-center justify-center">
                      <Icon size={20} className="text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {benefit.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* ROI Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-medical-blue text-white p-8 md:p-10">
          <div className="absolute inset-0 shimmer" />
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full" />

          <div className="relative z-10 text-center">
            <TrendingUp size={40} className="mx-auto mb-5 text-white/80" />

            <h3 className="text-xl md:text-2xl font-display font-bold mb-3">
              7/7 Metrikte Hedef Karşılandı
            </h3>

            <p className="text-sm opacity-90 mb-8 max-w-xl mx-auto">
              Arama 42ms, Benzerlik 25ms, Önbellek %75, WebSocket 88ms, AI 1.8s, 1,200 kullanıcı, 1.8GB bellek
            </p>

            <div className="grid grid-cols-3 gap-6 mb-8 max-w-md mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold mb-1">6 Ay</div>
                <div className="text-xs opacity-70">ROI Süresi</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-1">3x</div>
                <div className="text-xs opacity-70">Verimlilik</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-1">24/7</div>
                <div className="text-xs opacity-70">Hizmet</div>
              </div>
            </div>

            <button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-white text-primary-600 font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors duration-200 inline-flex items-center gap-2 text-sm"
            >
              <Zap size={16} />
              ROI Hesaplama Talep Et
            </button>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
